import React, { useState } from 'react';
import { Stack, Text, Card, Button, Group, Skeleton } from '@mantine/core';
import BigNumber from 'bignumber.js';
import cn from 'clsx';
import { NumberInput } from 'src/components/NumberInput/NumberInput';
import { WalletSuspense } from 'src/components/WalletSuspense/WalletSuspense';
import { ErrorDisplay } from 'src/components/ErrorDisplay/ErrorDisplay';
import { Rows } from 'src/components/Rows/Rows';
import { formatDecimal } from 'src/utils';
import {
  useSharesBalance,
  useAssetsToSharesRatio,
} from 'src/api/smart-contracts';
import {
  useWithdrawalWaitTime,
  useStakingIsPaused,
} from 'src/api/smart-contracts';
import { LatestRequest } from 'src/types';
import { SHOW_GAS_FEES } from 'src/constants';
import { useRequestRedeem } from './useRequestRedeem';

export function RequestPanel({
  heading,
  onRequestSuccess,
}: {
  heading: React.ReactNode;
  onRequestSuccess?: (latestRequest: LatestRequest) => void;
}) {
  const { stakingIsPaused, stakingIsPausedMessage } = useStakingIsPaused();
  const { sharesBalanceFormatted, isLoadingSharesBalance } = useSharesBalance();
  const { sharesToAssetsRatio, isLoadingRatio } = useAssetsToSharesRatio();

  const [value, setValue] = useState<string | undefined>();
  const [withdrawalMethod, setWithdrawalMethod] = useState<'apriori' | 'pool'>(
    'apriori'
  );

  const valueBN = BigNumber(value || 0);

  const { withdrawalWaitTimeHR, isLoadingWithdrawalWaitTime } =
    useWithdrawalWaitTime();
  const {
    isRequesting,
    requestError,
    transactionCost,
    isLoadingTransactionCost,
    requestRedeem,
  } = useRequestRedeem({
    amount: value || '0',
    onSuccess: (timestamp) => {
      onRequestSuccess?.({
        value: BigNumber(value!).times(1e18).toFixed(),
        timestamp,
      });
      setValue(undefined);
    },
  });

  const getSubmitErrorMessage = () => {
    if (BigNumber(valueBN).lte(0)) {
      return 'Enter an amount';
    }

    if (BigNumber(valueBN).gt(sharesBalanceFormatted)) {
      return 'Insufficient balance';
    }

    return '';
  };

  const submitErrorMessage = getSubmitErrorMessage();
  const submitDisabled =
    stakingIsPaused || !!submitErrorMessage || !requestRedeem;

  const handleSubmit = () => {
    if (submitDisabled) {
      return;
    }

    requestRedeem();
  };

  const waitTime = isLoadingWithdrawalWaitTime ? (
    <Skeleton w={160} h={23} />
  ) : (
    withdrawalWaitTimeHR
  );

  return (
    <Stack gap={24}>
      <Group justify="center">{heading}</Group>
      <form
        onSubmit={(e) => {
          e.preventDefault();
          handleSubmit();
        }}
        className={cn(
          (stakingIsPaused || isRequesting) && 'pointer-events-none'
        )}
      >
        <Stack gap={48}>
          <Stack gap={24}>
            <NumberInput
              value={value}
              label={
                <Group justify="space-between" w="100%">
                  <Text>Unstake aprMON</Text>
                  <Text className="flex items-center gap-2" td="underline">
                    Available:{' '}
                    {isLoadingSharesBalance ? (
                      <Skeleton w={40} h={23} />
                    ) : (
                      formatDecimal(sharesBalanceFormatted)
                    )}{' '}
                    aprMON
                  </Text>
                </Group>
              }
              token="APRMON"
              inputProps={{
                labelProps: { w: '100%' },
              }}
              onChange={setValue}
              onMaxClick={() => setValue(sharesBalanceFormatted)}
            />

            <Group gap={14} className="max-lg:flex-col">
              <ChoiceCard
                title="Use aPriori"
                rows={[
                  {
                    label: 'Rate',
                    value: isLoadingRatio ? (
                      <Skeleton w={40} h={23} />
                    ) : (
                      `1 : ${formatDecimal(sharesToAssetsRatio)}`
                    ),
                  },
                  {
                    label: 'Wait time',
                    value: waitTime,
                  },
                  {
                    label: 'You receive',
                    value: `${formatDecimal(BigNumber(value || 0).times(sharesToAssetsRatio))} MON`,
                  },
                ]}
                isActive={withdrawalMethod === 'apriori'}
                onClick={() => setWithdrawalMethod('apriori')}
              />
              <ChoiceCard
                title="Use Pool"
                rows={[
                  { label: 'Rate', value: '' },
                  { label: 'Wait time', value: '' },
                  {
                    label: 'You receive',
                    value: '',
                  },
                ]}
                isActive={withdrawalMethod === 'pool'}
                disabled
                onClick={() => setWithdrawalMethod('pool')}
              />
            </Group>
          </Stack>

          <Stack gap={24}>
            <WalletSuspense buttonProps={{ size: 'lg' }}>
              <Button
                variant="gradient"
                size="lg"
                type="submit"
                loading={isRequesting}
                disabled={submitDisabled}
              >
                {stakingIsPaused
                  ? stakingIsPausedMessage
                  : submitErrorMessage || 'Request Withdrawal'}
              </Button>

              {!submitErrorMessage && (
                <ErrorDisplay>{requestError?.message}</ErrorDisplay>
              )}
            </WalletSuspense>

            <div className="h-[1px] bg-main1 opacity-20" />

            <Stack gap={8}>
              <Rows
                items={[
                  ...(SHOW_GAS_FEES
                    ? [
                        {
                          label: 'Estimated transaction cost',
                          value: isLoadingTransactionCost ? (
                            <Skeleton w={160} h={23} />
                          ) : transactionCost.eq(0) ? (
                            '-'
                          ) : (
                            `${formatDecimal(transactionCost, { maxDecimals: 6 })} MON`
                          ),
                        },
                      ]
                    : []),
                  {
                    label: 'Estimated wait time',
                    value: waitTime,
                  },
                ]}
              />
            </Stack>
          </Stack>
        </Stack>
      </form>
    </Stack>
  );
}

function ChoiceCard({
  title,
  rows,
  isActive,
  disabled,
  onClick,
}: {
  title: string;
  rows: { label: string; value: React.ReactNode }[];
  isActive?: boolean;
  disabled?: boolean;
  onClick?: () => void;
}) {
  return (
    <Card
      p={16}
      className={cn(
        'relative flex-grow max-lg:w-full',
        isActive && 'bg-primarypurple/10',
        disabled && 'opacity-50 pointer-events-none',
        !disabled && 'cursor-pointer'
      )}
      onClick={onClick}
    >
      <Stack gap={4}>
        <Group justify="space-between">
          <Text fw={600}>{title}</Text>

          {disabled && <Text>Coming soon</Text>}
        </Group>

        <Stack gap={4}>
          <Rows items={rows} />
        </Stack>
      </Stack>

      <div
        className={cn(
          'z-10 pointer-events-none absolute inset-0',
          'rounded-[inherit] border-solid border-[2px] border-primarypurple transition-opacity duration-300',
          isActive ? 'opacity-100' : 'opacity-0'
        )}
      />
    </Card>
  );
}
